import { toast } from "@kyan/ui";
import { useCallback, useEffect } from "react";

import { usePortfolioValidations } from "#/hooks/usePortfolioValidations";
import { useEquityModalPortfolio } from "#/store/app/equityModal/equityModalPortfolio";
import { useEquityModalTab } from "#/store/app/equityModal/equityModalTab";
import { useShowEquityModal } from "#/store/app/equityModal/showEquityModal";
import { useLiquidationWarnings } from "#/store/app/liquidationWarnings/liquidationWarnings";
import { type UnderlyingSymbol } from "#/types/markets";

import { createDescription, createTitle } from "./LiquidationWarnings.utils";

export const useLiquidationWarningsController = () => {
  const {
    showLiquidationNotifications,
    liquidationWarningDetails,
    dismissLiquidationWarning,
  } = useLiquidationWarnings();
  const { setShowEquityModal } = useShowEquityModal();
  const { setEquityModalPortfolio } = useEquityModalPortfolio();
  const { setEquityModalTab } = useEquityModalTab();
  const { hasTransferableEquity } = usePortfolioValidations();
  const areLiquidationWarningsInvisible = liquidationWarningDetails.every(
    ({ variant }) => variant === "none",
  );
  const areAlertsInvisible =
    areLiquidationWarningsInvisible || !showLiquidationNotifications;

  const handleDeposit = useCallback(
    (symbol: UnderlyingSymbol) => {
      setShowEquityModal(true);
      setEquityModalPortfolio(symbol);
      setEquityModalTab(hasTransferableEquity ? "transfer" : "deposit");
    },
    [
      hasTransferableEquity,
      setEquityModalTab,
      setShowEquityModal,
      setEquityModalPortfolio,
    ],
  );

  useEffect(() => {
    if (areLiquidationWarningsInvisible) return;

    liquidationWarningDetails.forEach(({ symbol, variant, mmr }) => {
      if (variant === "none") return;

      // Static toast ids are required to show a single toast for each symbol and variant combination
      const currentToastId = `${symbol}-${variant}`;

      const toastMethod =
        variant === "warning"
          ? toast.liquidationWarning
          : toast.liquidationError;

      toastMethod({
        id: currentToastId,
        title: createTitle(symbol, variant),
        description: createDescription({ symbol, variant, mmr, isToast: true }),
        duration: Infinity,
        action: {
          label: "Deposit",
          onClick: () => {
            handleDeposit(symbol);
          },
        },
        onDismiss: () => {
          dismissLiquidationWarning(symbol);
        },
        onAutoClose: () => {
          dismissLiquidationWarning(symbol);
        },
      });
    });
  }, [
    liquidationWarningDetails,
    areLiquidationWarningsInvisible,
    handleDeposit,
    dismissLiquidationWarning,
  ]);

  return {
    areAlertsInvisible,
    liquidationWarningDetails,
    showLiquidationNotifications,
    handleDeposit,
    dismissLiquidationWarning,
  };
};
